//Libs
import { Fragment, memo, useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  CellContextMenuEvent,
  ColDef,
  GetQuickFilterTextParams,
  ICellRendererParams,
  RowClickedEvent,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
} from "ag-grid-community";
import isEqual from "lodash/isEqual";
import PencilIcon from "@heroicons/react/24/outline/PencilIcon";
import PlusIcon from "@heroicons/react/24/outline/PlusIcon";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { Link } from "react-router-dom";

//Local
import { ExistingVendor } from "../../models/vendor";
import * as strings from "../../strings";
import { phoneUtils } from "../../utils/phoneUtils";
import {
  handleDeleteTableView,
  handleSetDefaultView,
  TableViewPreference,
} from "../../models/table-view-preferences";
import EditTableViewsDialog from "../../components/tables/EditTableViewsDialog";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import { VENDORS_URL, NEW_PURCHASE_ORDER_URL } from "../../urls";
import SearchBox from "../../components/SearchBox";
import HeadingOne from "../../components/HeadingOne";
import VendorDialog from "../../components/vendors/VendorDialog";
import { VendorFormValues } from "../../components/vendors/VendorForm";
import { DbWrite } from "../../database";
import BaseInputText from "../../components/BaseInputText";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import { logger } from "../../logging";
import LoadingSpinner from "../../components/LoadingSpinner";

// Styles
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import { useAuthStore } from "../../store/firebase-auth";

interface Props {
  vendorList: ExistingVendor[];
  siteKey: string;
  goToViewVendor: (vendor: ExistingVendor) => void;
  handleVendorLogo: (selectedFile: File) => Promise<string>;
  handlePreviewAndErrors: (event: React.ChangeEvent<HTMLInputElement>) => void;
  preview: string | null;
  selectedFile: File | null;
  fileInformation: string;
  responseToUserError: boolean;
  resetLogoUploadState: () => void;
}

export default function VendorListPage(props: Props) {
  const firebaseUser = useAuthStore((state) => state.firebaseUser);

  const [tableViewPreferenceDialogOpen, setTableViewPreferenceDialogOpen] =
    useState(false);
  const [editViewDialogsOpen, setEditViewDialogsOpen] = useState(false);
  const [tableViewPreferenceList, setTableViewPreferenceList] = useState<
    TableViewPreference[]
  >([]);
  const [selectedView, setSelectedView] = useState<TableViewPreference | null>(
    null,
  );
  const [addVendorDialogOpen, setAddVendorDialogOpen] = useState(false);
  const [editVendorDialogOpen, setEditVendorDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<ExistingVendor | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const gridRef = useRef<AgGridReact>(null);

  // Google Places autocomplete state
  const [address, setAddress] = useState("");
  const [geocoderResult, setGeocoderResult] =
    useState<google.maps.GeocoderResult | null>(null);
  const [showManualEntryForm, setShowManualEntryForm] = useState(false);
  const [displayAddressError, setDisplayAddressError] = useState(false);

  // Handle saving new vendor with optional logo
  const handleSaveNewVendor = async (formValues: VendorFormValues) => {
    try {
      if (!firebaseUser) throw Error("No Firebase user was found");

      setIsSubmitting(true);

      let logoURL = null;

      // If there's a selected file, upload it first
      if (props.selectedFile) {
        logoURL = await props.handleVendorLogo(props.selectedFile);
      }

      // Create vendor with logo URL, overriding the form's logoURL
      await DbWrite.vendors.create({
        ...formValues,
        logoURL,
        siteKey: props.siteKey,
        createdBy: firebaseUser.uid,
        lastModifiedBy: firebaseUser.uid,
      });

      props.resetLogoUploadState();
      return Promise.resolve();
    } catch (error) {
      console.error("Error creating vendor:", error);
      return Promise.reject(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle saving edited vendor with optional logo
  const handleSaveEditVendor = async (formValues: VendorFormValues) => {
    try {
      if (!firebaseUser) throw Error("No Firebase user was found");
      if (!selectedVendor) throw Error("No vendor selected");

      setIsSubmitting(true);

      let logoURL = selectedVendor.logoURL;

      // If there's a new file selected, upload it first
      if (props.selectedFile) {
        logoURL = await props.handleVendorLogo(props.selectedFile);
      }

      // Update vendor with new data and logo URL
      await DbWrite.vendors.update({
        id: selectedVendor.id,
        refPath: selectedVendor.refPath,
        ...formValues,
        logoURL,
        lastModifiedBy: firebaseUser.uid,
      });

      props.resetLogoUploadState();
      return Promise.resolve();
    } catch (error) {
      console.error("Error updating vendor:", error);
      return Promise.reject(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const actionButtons = (
    <Fragment>
      {/* <BaseButtonPrimary onClick={() => setTableViewPreferenceDialogOpen(true)}>
        {strings.SAVE_TABLE_VIEW}
      </BaseButtonPrimary>
      <BaseButtonSecondary onClick={() => setEditViewDialogsOpen(true)}>
        {strings.EDIT_TABLE_VIEWS}
      </BaseButtonSecondary> */}
      <Link to={NEW_PURCHASE_ORDER_URL}>
        <BaseButtonSecondary>Create Purchase Order</BaseButtonSecondary>
      </Link>
      <BaseButtonPrimary onClick={() => setAddVendorDialogOpen(true)}>
        {" "}
        {strings.NEW_VENDOR}
      </BaseButtonPrimary>
    </Fragment>
  );

  const editTableViewsDialog = (
    <EditTableViewsDialog
      isDialogOpen={editViewDialogsOpen}
      closeDialog={() => setEditViewDialogsOpen(false)}
      deleteTableView={(viewToDelete: TableViewPreference) =>
        handleDeleteTableView(
          viewToDelete,
          setTableViewPreferenceList,
          setSelectedView,
        )
      }
      handleSetDefaultView={(viewToSetAsDefault: TableViewPreference) =>
        handleSetDefaultView(
          viewToSetAsDefault,
          setTableViewPreferenceList,
          setSelectedView,
        )
      }
      savedViewList={tableViewPreferenceList}
    />
  );

  // Vendor Logo Section
  const vendorLogoSection = (
    <div className="relative grid grid-cols-1 justify-items-center rounded border border-black p-6 sm:grid-cols-2 md:pl-2 md:pr-0 lg:col-span-2 lg:px-4">
      <label className="absolute -top-3 left-3 bg-white px-2 xs:col-span-2 sm:col-span-1 md:col-span-2">
        Vendor Logo
      </label>
      <label
        htmlFor="vendor-img-upload"
        className="col-span-2 inline-flex h-10 w-full max-w-xs items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primaryButtonText shadow-sm hover:bg-primaryDark hover:text-secondaryButtonText focus:outline-none focus:ring-2 focus:ring-primaryLight focus:ring-offset-2 "
      >
        Upload
      </label>
      <input
        id="vendor-img-upload"
        type="file"
        onChange={(ev) => props.handlePreviewAndErrors(ev)}
        accept="image/jpg, image/jpeg, image/png"
        className="hidden"
      />

      <span
        className={
          props.responseToUserError
            ? "text-base font-medium text-red-800"
            : "hidden"
        }
      >
        {props.fileInformation}
      </span>

      {props.selectedFile && props.preview && (
        <img
          src={props.preview}
          alt="Vendor logo preview"
          className="col-span-2 mt-3 w-48 rounded-md sm:w-64"
        />
      )}
      {!props.selectedFile &&
        !props.preview &&
        editVendorDialogOpen &&
        selectedVendor?.logoURL && (
          <img
            src={selectedVendor.logoURL}
            alt="Current vendor logo"
            className="col-span-2 mt-3 w-48 rounded-md sm:w-64"
          />
        )}
    </div>
  );

  async function handleSelectAddress(selectedAddress: string) {
    try {
      const results = await geocodeByAddress(selectedAddress);
      if (results && results.length > 0) {
        setGeocoderResult(results[0]);
        setDisplayAddressError(false);
        logger.debug("geocoder result", results[0]);
      }
    } catch (error) {
      logger.error("Error selecting address", error);
      setDisplayAddressError(true);
    }
  }

  function resetAddressState() {
    setAddress("");
    setGeocoderResult(null);
    setShowManualEntryForm(false);
    setDisplayAddressError(false);
  }

  function getAddressFieldComponent() {
    return (
      <PlacesAutocomplete
        value={address}
        onChange={(newAddress: string) => {
          if (newAddress === "") {
            setDisplayAddressError(true);
            setAddress(newAddress);
          } else {
            setAddress(newAddress);
            setDisplayAddressError(false);
          }
        }}
        onSelect={handleSelectAddress}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <div className="w-full">
            <BaseInputText
              {...getInputProps({
                admin: true,
                required: true,
                inputName: "fullAddress",
                text: "Address",
              })}
            />
            <div
              className={
                suggestions.length > 0 || loading
                  ? "absolute z-10 max-w-sm rounded border bg-white p-2"
                  : ""
              }
            >
              {loading ? (
                <div className="p-2 text-primary">
                  <LoadingSpinner />
                </div>
              ) : null}

              {suggestions.map((suggestion, suggestionIdx) => {
                const style = {
                  backgroundColor: suggestion.active
                    ? "var(--primary-opacity-90)"
                    : "#fff",
                  borderRadius: "0.25rem",
                  padding: "5px",
                };
                return (
                  <p
                    {...getSuggestionItemProps(suggestion, { style })}
                    key={suggestionIdx}
                  >
                    {suggestion.description}
                  </p>
                );
              })}
            </div>
          </div>
        )}
      </PlacesAutocomplete>
    );
  }

  return (
    <div className="space-y-2 lg:space-y-6">
      <HeadingOne>{strings.VENDORS}</HeadingOne>
      <div className="ag-theme-alpine isolate h-[1000px] max-h-[68vh] pb-6">
        <VendorListTable
          vendorList={props.vendorList}
          goToViewVendor={(vendor: ExistingVendor) => {
            setSelectedVendor(vendor);
            // Pre-populate address field with vendor's current address when editing
            if (vendor.addressLine1 && vendor.city && vendor.state) {
              const fullAddress = `${vendor.addressLine1}${vendor.addressLine2 ? `, ${vendor.addressLine2}` : ""}, ${vendor.city}, ${vendor.state} ${vendor.zipCode}${vendor.country && vendor.country !== "USA" ? `, ${vendor.country}` : ""}`;
              setAddress(fullAddress);
            } else {
              setAddress("");
            }
            setEditVendorDialogOpen(true);
          }}
          actionButtons={actionButtons}
          gridRef={gridRef}
          setSelectedView={setSelectedView}
          setTableViewPreferenceList={setTableViewPreferenceList}
          selectedView={selectedView}
        />
      </div>
      {editTableViewsDialog}
      <VendorDialog
        isDialogOpen={addVendorDialogOpen}
        geocoderResult={geocoderResult || ({} as google.maps.GeocoderResult)}
        showManualEntryForm={showManualEntryForm}
        setShowManualEntryForm={setShowManualEntryForm}
        closeDialog={() => {
          setAddVendorDialogOpen(false);
          resetAddressState();
          props.resetLogoUploadState();
        }}
        handleSave={handleSaveNewVendor}
      >
        {{
          VendorLogoSection: vendorLogoSection,
          AddressField: getAddressFieldComponent(),
        }}
      </VendorDialog>

      {/* Edit Vendor Dialog */}
      {selectedVendor && (
        <VendorDialog
          isDialogOpen={editVendorDialogOpen}
          vendor={selectedVendor}
          geocoderResult={geocoderResult || ({} as google.maps.GeocoderResult)}
          showManualEntryForm={showManualEntryForm}
          setShowManualEntryForm={setShowManualEntryForm}
          closeDialog={() => {
            setEditVendorDialogOpen(false);
            setSelectedVendor(null);
            resetAddressState();
            props.resetLogoUploadState();
          }}
          handleSave={handleSaveEditVendor}
        >
          {{
            VendorLogoSection: vendorLogoSection,
            AddressField: getAddressFieldComponent(),
          }}
        </VendorDialog>
      )}
    </div>
  );
}

interface VendorListTableProps {
  vendorList: Props["vendorList"];
  goToViewVendor: Props["goToViewVendor"];
  actionButtons: React.ReactNode;
  gridRef: React.MutableRefObject<any>;
  setSelectedView: React.Dispatch<
    React.SetStateAction<TableViewPreference | null>
  >;
  setTableViewPreferenceList: React.Dispatch<
    React.SetStateAction<TableViewPreference[]>
  >;
  selectedView: TableViewPreference | null;
}

const commonColDefs: ColDef = {
  resizable: true,
  filter: true,
  sortable: true,
  headerClass:
    "px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase",
  flex: 1,
  tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
  tooltipField: "notes",
};

/* TABLE COMPONENT */
const VendorListTable = memo(
  ({
    vendorList,
    goToViewVendor,
    actionButtons,
    gridRef,
    setSelectedView,
    setTableViewPreferenceList,
    selectedView,
  }: VendorListTableProps) => {
    const [gridReady, setGridReady] = useState(false);

    const autoSizeStrategy = useMemo<
      | SizeColumnsToFitGridStrategy
      | SizeColumnsToFitProvidedWidthStrategy
      | SizeColumnsToContentStrategy
    >(() => {
      return {
        type: "fitGridWidth",
        defaultMinWidth: 50,
        columnLimits: [
          {
            colId: "ID",
            minWidth: 50,
          },
        ],
      };
    }, []);

    const onGridReady = useCallback(() => {
      setGridReady(true);
    }, []);

    // useLayoutEffect(() => {
    //   if (gridRef.current && gridReady) {
    //     gridRef.current.api?.sizeColumnsToFit();
    //     getTableViewPreferenceList("vendorList", setTableViewPreferenceList);
    //     const defaultView = getDefaultViewForThisTable("vendorList");
    //     setInitialView(defaultView, gridRef, setSelectedView);
    //   }
    // }, [gridRef, setSelectedView, setTableViewPreferenceList, gridReady]);

    // useEffect(() => {
    //   if (selectedView && gridReady) {
    //     gridRef.current?.api.applyColumnState({
    //       state: selectedView.settings,
    //       applyOrder: true,
    //     });
    //     gridRef.current?.api.setFilterModel(selectedView.filter);
    //     logger.info("set column state for", selectedView.name);
    //   }
    // }, [gridRef, selectedView, gridReady]);

    const onFilterTextBoxChanged = useCallback(() => {
      if (gridReady) {
        gridRef.current?.api.setQuickFilter(
          (document.getElementById("filter-text-box") as HTMLInputElement)
            .value,
        );
      }
    }, [gridRef, gridReady]);

    const renderPhones = (params: ICellRendererParams): JSX.Element => {
      const phones = params.data.phone;
      if (!phones || !phones.length) return <div></div>;

      return (
        <div>
          {phones.map((phone: string, index: number) => (
            <div key={index}>{phoneUtils.display(phone)}</div>
          ))}
        </div>
      );
    };

    const renderEmails = (params: ICellRendererParams): JSX.Element => {
      const emails = params.data.email;
      if (!emails || !emails.length) return <div></div>;

      return (
        <div>
          {emails.map((email: string, index: number) => (
            <div key={index}>{email}</div>
          ))}
        </div>
      );
    };

    const renderName = (params: ICellRendererParams): JSX.Element => {
      const {
        name,
        addressLine1,
        addressLine2,
        city,
        state,
        zipCode,
        country,
        logoURL,
      } = params.data;

      const fullAddress = [
        addressLine1,
        addressLine2,
        `${city}, ${state} ${zipCode}`,
        country,
      ]
        .filter(Boolean)
        .join("\n");

      return (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            {logoURL ? (
              <img
                src={logoURL}
                alt={`${name} logo`}
                className="h-10 w-10 rounded-md border border-gray-200 object-cover"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 bg-gray-100">
                <span className="text-xs font-medium text-gray-400">
                  {name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="block truncate text-base font-medium leading-none">
              {name}
            </span>
            <span className="mt-1 block text-sm leading-none text-gray-400">
              {fullAddress}
            </span>
          </div>
        </div>
      );
    };

    const renderTags = (params: ICellRendererParams): JSX.Element => {
      const tags = params.data.tags;
      if (!tags || !tags.length) return <div></div>;

      return (
        <div className="flex flex-wrap gap-1">
          {tags.map((tag: string, index: number) => (
            <span
              key={index}
              className="inline-block rounded-full bg-gray-200 px-2 py-1 text-xs font-semibold text-gray-700"
            >
              {tag}
            </span>
          ))}
        </div>
      );
    };

    const columnDefs: ColDef[] = [
      {
        field: "name",
        headerName: "Vendor",
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
        cellRenderer: renderName,
        width: 250,
        tooltipField: "name",
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "phone",
        headerName: "Phone",
        cellRenderer: renderPhones,
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "email",
        headerName: "Email",
        cellRenderer: renderEmails,
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "website",
        headerName: "Website",
        cellRenderer: (params: ICellRendererParams) =>
          params.data.website ? (
            <a
              href={params.data.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {params.data.website}
            </a>
          ) : null,
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "paymentTerms",
        headerName: "Payment Terms",
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
      {
        field: "tags",
        headerName: "Tags",
        cellRenderer: renderTags,
        getQuickFilterText: (params: GetQuickFilterTextParams) =>
          params.value ? params.value.join(" ") : "",
        cellStyle: {
          height: "100%",
          display: "flex",
          alignItems: "center",
        },
      },
    ];

    return (
      <Fragment>
        <div className="flex flex-col-reverse justify-between gap-6 md:-mt-4 md:flex-row md:items-end lg:mt-0">
          <SearchBox
            onInput={onFilterTextBoxChanged}
            widthClasses="w-full max-w-sm"
          />
          <span className="flex flex-wrap items-center justify-end gap-4 md:flex-col md:items-end lg:flex-row lg:items-center">
            <div className="flex gap-4">{actionButtons}</div>
          </span>
        </div>
        <AgGridReact
          reactiveCustomComponents
          ref={gridRef}
          onGridReady={onGridReady}
          tooltipShowDelay={1}
          defaultColDef={commonColDefs}
          className="mt-5 shadow"
          rowData={vendorList}
          animateRows={true}
          rowSelection="single"
          cacheQuickFilter={true}
          autoSizeStrategy={autoSizeStrategy}
          columnDefs={columnDefs}
          onRowClicked={(event: RowClickedEvent) => goToViewVendor(event.data)}
          onCellContextMenu={(event: CellContextMenuEvent<any, any>) => {
            const url = `${VENDORS_URL}/${event.data.id}`;
            return window.open(url, "_blank");
          }}
        />
      </Fragment>
    );
  },
  (previous, next) => {
    const isVendorListTheSame = isEqual(previous.vendorList, next.vendorList);

    return isVendorListTheSame;
  },
);
